import { useToast } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { Alert } from "../alert/Alert";
import { createGlobalStyle } from "styled-components";
import { CSS_Notifications } from "./NotificationsCSS";
import { useIsMedia } from "src/apps/hooks/useIsMedia";
import { useNotifications } from "src/apps/hooks/useNotifications";
import { NotificationStyles } from "src/contexts/_shared/domain/NotificationStyles";
import { ICON_PATHS } from "src/apps/components/icon/IconSVGPaths";
import { useTheme } from "src/apps/providers/ThemeStore";
import { Notification } from "src/contexts/_shared/domain/Notification";
import { colors } from "@permisso/permisso-components";
import { getColorWithOpacity } from "src/apps/config/colors";

const MAX_VISIBLE_NOTIFICATIONS = 3;

export const Notifications = () => {
  const { notifications } = useNotifications();
  const { errorColor, successColor } = useTheme();
  const { isMobile } = useIsMedia();
  const toast = useToast();

  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());

  const visibleNotifications = useMemo(() => {
    const sorted = [...notifications].sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    const sliced = sorted.slice(0, MAX_VISIBLE_NOTIFICATIONS);
    return sliced;
  }, [notifications]);

  const renderAlert = (notification: Notification) => {
    const alertStyle = NotificationsStylesMapper[notification.style];
    return Alert({
      backgroundColor: alertStyle.backgroundColor,
      color: alertStyle.color,
      title: notification.title,
      message: notification.message,
      messageParams: notification.messageParams,
      icon: alertStyle.icon,
      onClose: () => {
        toast.close(notification.id);
        notification.onClose?.(notification.id);
      },
    });
  };

  const NotificationsStylesMapper = {
    [NotificationStyles.ALERT]: {
      backgroundColor: getColorWithOpacity(errorColor, 0.2),
      color: errorColor,
      icon: ICON_PATHS.MD_CANCEL,
    },
    [NotificationStyles.WARNING]: {
      backgroundColor: getColorWithOpacity(colors.darkYellow, 0.2),
      color: colors.darkYellow,
      icon: ICON_PATHS.ALERT,
    },
    [NotificationStyles.SUCCESS]: {
      backgroundColor: getColorWithOpacity(successColor, 0.2),
      color: successColor,
      icon: ICON_PATHS.CK_CHECK_CIRCLE,
    },
    [NotificationStyles.INFO]: {
      backgroundColor: getColorWithOpacity(colors.defaultBlue, 0.2),
      color: colors.defaultBlue,
      icon: ICON_PATHS.MD_INFO,
    },
  };

  useEffect(() => {
    const prevIds = visibleIds;
    const nextIds = new Set(visibleNotifications.map((n) => n.id));

    // close those that are pushed out by new notifications
    prevIds.forEach((id) => {
      if (!nextIds.has(id)) {
        toast.close(id);
      }
    });

    // open any newly added notifications
    visibleNotifications.forEach((n) => {
      if (!toast.isActive(n.id)) {
        toast({
          id: n.id,
          position: isMobile ? "top" : "top-right",
          duration: null,
          render: () => renderAlert(n),
        });
      }
    });

    setVisibleIds(nextIds);
  }, [visibleNotifications]);

  useEffect(() => {
    // reposition notifications when mobile layout changes
    toast.closeAll();
    visibleNotifications.forEach((n) => {
      toast({
        id: n.id,
        position: isMobile ? "top" : "top-right",
        duration: null,
        render: () => renderAlert(n),
      });
    });
  }, [isMobile]);

  return <NotificationsStyle />;
};

export const NotificationsStyle = createGlobalStyle`
  ${CSS_Notifications}
`;
